import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, MenuItem, Tooltip, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  EmissaoStatus,
  GETInstrumentosFilterParams,
  InstrumentoFinanceiro,
  InstrumentoFinanceiroApiResponse,
  RegistroStatus,
} from 'common/genericTypes';
import { DataGridPro, GridCellParams, GridColDef, GridLocaleText, GridValueGetterParams, getGridDateOperators, getGridSingleSelectOperators, getGridStringOperators } from '@mui/x-data-grid-pro';
import { componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { formatDate } from 'utils/date';
import { Description } from '@material-ui/icons';
import MoreOptionsMenu from 'components/moreOptionsMenu';

interface Props {
  administradorFiduciarioId: string;
}

const localizedTextsMap = {
  toolbarExport: 'Exportar',
  toolbarExportCSV: 'Exportar em CSV',
  toolbarExportPrint: 'Imprimir',
  columnMenuUnsort: 'Não classificado',
  columnMenuSortAsc: 'Ordem crescente',
  columnMenuSortDesc: 'Ordem decrescente',
  columnMenuFilter: 'Filtro',
  columnMenuHideColumn: 'Ocultar',
  columnMenuShowColumns: 'Mostrar colunas',
  columnsPanelHideAllButton: 'Ocultar todos',
  columnsPanelShowAllButton: 'Exibir todos',
  columnsPanelTextFieldLabel: 'Pesquisar coluna',
  columnsPanelTextFieldPlaceholder: 'Nome da coluna',
  columnHeaderSortIconLabel: 'Ordernar',
  MuiTablePagination: {
    labelRowsPerPage: 'Linhas por página',
    labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} de ${count}`,
  },
} as Partial<GridLocaleText>;

const FundosDepositados: React.FC<Props> = ({ administradorFiduciarioId }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const getInstrumentosPaginated = useSelector((state: RootState) => state.instrumentoApi.postInstrumentosPaginated);
  const getCotasDeFundoByInstrumentoId = useSelector((state: RootState) => state.cotasDeFundoApi.getCotasDeFundoByInstrumentoId);

  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [instrumentoList, setInstrumentoList] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);

  const filterBase: GETInstrumentosFilterParams = useMemo(() => ({
    registroStatus: [RegistroStatus.Aprovado, RegistroStatus.AguardandoAprovacaoEdicao, RegistroStatus.AguardandoAprovacaoAditamento],
    page: page + 1,
    pageSize,
    tipoInstrumento: [InstrumentoFinanceiro.CotasDeFundo],
  }), [page, pageSize]);

  const carregarLista = useCallback(async () => {
    try {
      const result = await getInstrumentosPaginated(
        dispatch,
        filterBase,
        true,
        null,
        null,
        null,
        null,
        null,
        administradorFiduciarioId
      );
      setInstrumentoList(result.result);
      setPageCount(result.totalCount);
    } catch {}
  }, [dispatch, getInstrumentosPaginated, filterBase, administradorFiduciarioId]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  const handleVerDetalhes = useCallback(async (instrumento: InstrumentoFinanceiroApiResponse) => {
    // Apenas COTAS_FUNDO são listados aqui, então podemos resolver o ID e navegar
    const detalhes = await getCotasDeFundoByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
    history.push(`/instrumentos/gerenciar/cotas-de-fundos/${detalhes.cotasDeFundoId}`, { detalhes });
  }, [dispatch, getCotasDeFundoByInstrumentoId, history]);

  const filterOperators = getGridStringOperators().filter(({ value }) => ['equals', 'contains', 'startsWith', 'endsWith'].includes(value));
  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) => ['is', 'not'].includes(value));
  const filterOperatorsData = getGridDateOperators();

  const columns = useMemo<GridColDef[]>(() => ([
    {
      field: 'Codigo', headerName: 'Código', type: 'string', filterOperators, filterable: true, sortable: true,
      width: 180, minWidth: 100, maxWidth: 220,
      valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).codigo,
    },
    {
      field: 'Emissor', headerName: 'Emissor', type: 'string', filterOperators, filterable: true, sortable: true,
      width: 220, minWidth: 120, maxWidth: 280,
      valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).razaoSocialEmissor,
    },
    {
      field: 'Status', headerName: 'Status Emissão', type: 'singleSelect', valueOptions: ['Emitido', 'Aguardando Liquidação', 'Não Emitido', 'Parcialmente Emitido'],
      filterOperators: filterOperatorsSelect, filterable: true, sortable: true,
      width: 160, minWidth: 120, maxWidth: 200,
      valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).statusEmissao,
      renderCell: (params: GridCellParams) => {
        const status = (params.row as InstrumentoFinanceiroApiResponse).statusEmissao;
        let label = '-';
        if (status === EmissaoStatus.Emitido) label = 'Emitido';
        if (status === EmissaoStatus.AguardandoLiquidacao) label = 'Aguardando Liquidação';
        if (status === EmissaoStatus.NaoEmitido) label = 'Não Emitido';
        if (status === EmissaoStatus.ParcialmenteEmitido) label = 'Parcialmente Emitido';
        return <>{label}</>;
      }
    },
    {
      field: 'DATA_EMISSAO', headerName: 'Data da Emissão', type: 'date', filterOperators: filterOperatorsData,
      filterable: true, sortable: true, width: 140, minWidth: 120, maxWidth: 200,
      valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).criteriosRemuneracao?.dataEmissao,
      renderCell: (params: GridCellParams) => {
        const d = (params.row as InstrumentoFinanceiroApiResponse).criteriosRemuneracao?.dataEmissao;
        return <>{d ? formatDate(d) : '-'}</>;
      }
    },
    {
      field: 'Acoes', headerName: '', type: 'string', filterOperators, filterable: false, sortable: false, disableExport: true,
      width: 100, minWidth: 100, maxWidth: 150,
      renderCell: (params: GridCellParams) => {
        const instrumento = params.row as InstrumentoFinanceiroApiResponse;
        return (
          <MoreOptionsMenu>
            <MenuItem onClick={() => handleVerDetalhes(instrumento)}>
              <Description fontSize="small" style={{ marginRight: '10px' }} />
              {'Detalhes'}
            </MenuItem>
          </MoreOptionsMenu>
        );
      },
    },
  ]), [filterOperators, filterOperatorsSelect, filterOperatorsData, handleVerDetalhes]);

  return (
    <>
      <Grid item md={12} style={{ marginTop: 16 }}>
        <DataGridPro
          autoHeight
          localeText={localizedTextsMap}
          componentsProps={componentsPropsValues}
          rows={instrumentoList}
          columns={columns}
          components={{
            Toolbar: CustomToolBarUtils,
          }}
          disableSelectionOnClick
          rowsPerPageOptions={rowsPerPageOptionsValues}
          page={page}
          rowCount={pageCount}
          pagination
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(0); }}
          pageSize={pageSize}
        />
      </Grid>
    </>
  );
};

export default FundosDepositados;
